/**
 * @fileoverview Message bubble component for individual SMS/MMS messages.
 * Displays message content with directional styling, metadata indicators,
 * and media attachments (images/videos) for MMS messages.
 *
 * MMS media is fetched via Calie_getMMSMedia edge function to handle
 * Twilio's HTTP Basic Auth requirement.
 */

import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { Check, AlertTriangle, Image as ImageIcon, X, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import type { SMSConversation } from '@/types/message';

/**
 * Props for MessageBubble component.
 */
interface MessageBubbleProps {
  /** The SMS message data to display */
  message: SMSConversation;
}

/** Media attachment type from Twilio */
interface MediaAttachment {
  url: string;
  contentType: string;
}

/** Cache for loaded media to avoid re-fetching */
const mediaCache = new Map<string, string>();

/**
 * Component to render a single MMS media item with loading state.
 * Fetches media via Calie_getMMSMedia edge function to handle Twilio auth.
 */
function MMSMediaItem({
  media,
  index,
  isOutbound,
  onImageClick,
}: {
  media: MediaAttachment;
  index: number;
  isOutbound: boolean;
  onImageClick: (dataUrl: string) => void;
}) {
  const [dataUrl, setDataUrl] = useState<string | null>(mediaCache.get(media.url) || null);
  const [isLoading, setIsLoading] = useState(!mediaCache.has(media.url));
  const [error, setError] = useState<string | null>(null);

  const fetchMedia = useCallback(async () => {
    // Check cache first
    if (mediaCache.has(media.url)) {
      setDataUrl(mediaCache.get(media.url)!);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Get Twilio account SID from tenant settings
      const { data: settings, error: settingsError } = await supabase
        .from('calie_tenants')
        .select('twilio_sid')
        .maybeSingle();

      if (settingsError || !settings?.twilio_sid) {
        throw new Error('Failed to get Twilio credentials');
      }

      // Get auth session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      // Fetch media via edge function
      const { data, error: fetchError } = await supabase.functions.invoke('Calie_getMMSMedia', {
        body: {
          mediaUrl: media.url,
          accountSid: settings.twilio_sid,
          contentType: media.contentType,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (fetchError || !data?.success) {
        throw new Error(fetchError?.message || data?.error || 'Failed to fetch media');
      }

      const url = `data:${data.contentType};base64,${data.base64Data}`;
      mediaCache.set(media.url, url);
      setDataUrl(url);
    } catch (err) {
      console.error('[MMSMediaItem] Error fetching media:', err);
      setError(err instanceof Error ? err.message : 'Failed to load media');
    } finally {
      setIsLoading(false);
    }
  }, [media.url, media.contentType]);

  useEffect(() => {
    if (!dataUrl && !error) {
      fetchMedia();
    }
  }, [dataUrl, error, fetchMedia]);

  const isImage = media.contentType?.startsWith('image/');
  const isVideo = media.contentType?.startsWith('video/');

  // Loading state
  if (isLoading) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-lg',
          isImage ? 'w-48 h-48' : 'w-48 h-12',
          isOutbound ? 'bg-primary-foreground/20' : 'bg-background/50'
        )}
      >
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg text-sm',
          isOutbound ? 'bg-primary-foreground/20' : 'bg-background'
        )}
      >
        <AlertCircle className="w-4 h-4 text-destructive" />
        <span className="text-destructive">Failed to load</span>
      </div>
    );
  }

  // Render image
  if (isImage && dataUrl) {
    return (
      <button
        onClick={() => onImageClick(dataUrl)}
        className="block cursor-pointer hover:opacity-90 transition-opacity"
      >
        <img
          src={dataUrl}
          alt={`Attachment ${index + 1}`}
          className="max-w-full rounded-lg max-h-64 object-cover"
        />
      </button>
    );
  }

  // Render video
  if (isVideo && dataUrl) {
    return (
      <video
        src={dataUrl}
        controls
        className="max-w-full rounded-lg max-h-64"
      />
    );
  }

  // Other file types - download link
  return (
    <a
      href={dataUrl || '#'}
      download={`attachment-${index + 1}`}
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-lg text-sm',
        isOutbound ? 'bg-primary-foreground/20' : 'bg-background'
      )}
    >
      <ImageIcon className="w-4 h-4" />
      <span>Attachment {index + 1}</span>
    </a>
  );
}

/**
 * Renders a chat-style message bubble with inbound/outbound styling.
 * Shows timestamp, SLA status, latency, auto-reply indicators, and media attachments.
 */
export function MessageBubble({ message }: MessageBubbleProps) {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const isOutbound = message.message_direction === 'outbound';
  const isAutoReply = message.metadata?.auto_reply;
  const mediaUrls = (message.metadata?.media_urls as MediaAttachment[] | undefined) || [];
  const hasMedia = mediaUrls.length > 0;
  const hasTextContent = message.message_body && message.message_body !== '[Media message]';

  return (
    <>
      <div className={cn('flex flex-col gap-1', isOutbound ? 'items-end' : 'items-start')}>
        <div
          className={cn(
            'max-w-[80%] rounded-2xl overflow-hidden',
            isOutbound
              ? 'bg-primary text-primary-foreground rounded-br-md'
              : 'bg-muted text-foreground rounded-bl-md',
            // Remove padding if only media (no text)
            hasTextContent || !hasMedia ? 'px-4 py-2' : 'p-1'
          )}
        >
          {/* Media attachments */}
          {hasMedia && (
            <div className={cn('flex flex-col gap-2', hasTextContent ? 'mb-2' : '')}>
              {mediaUrls.map((media, index) => (
                <MMSMediaItem
                  key={`${media.url}-${index}`}
                  media={media}
                  index={index}
                  isOutbound={isOutbound}
                  onImageClick={setLightboxImage}
                />
              ))}
            </div>
          )}

          {/* Text content */}
          {hasTextContent && (
            <p className="text-sm whitespace-pre-wrap break-words">{message.message_body}</p>
          )}
        </div>

        <div className="flex items-center gap-2 px-1">
          <span className="text-xs text-muted-foreground">
            {format(new Date(message.sent_at), 'h:mm a')}
          </span>

          {isOutbound && (
            <>
              {isAutoReply && (
                <span className="text-xs text-muted-foreground italic">Auto-reply</span>
              )}

              {message.sla_met !== null && (
                <span
                  className={cn(
                    'flex items-center gap-0.5 text-xs',
                    message.sla_met ? 'text-green-600' : 'text-amber-600'
                  )}
                >
                  {message.sla_met ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    <AlertTriangle className="w-3 h-3" />
                  )}
                  {message.latency_ms && `${Math.round(message.latency_ms)}ms`}
                </span>
              )}
            </>
          )}

          {!isOutbound && message.metadata?.from_city && (
            <span className="text-xs text-muted-foreground">
              {message.metadata.from_city}, {message.metadata.from_state}
            </span>
          )}
        </div>
      </div>

      {/* Lightbox modal for full-size image viewing */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300"
            onClick={() => setLightboxImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={lightboxImage}
            alt="Full size"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
